import { useEffect, useState } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import {
    Calendar,
    ChartColumnBig,
    ChevronDown,
    FileText,
    LayoutGrid,
    LucideIcon,
    Monitor,
} from 'lucide-react';
import {
    SidebarDivider,
    SidebarIcon,
    sidebarItemClassName,
} from 'src/components/Buttons/NavItem';
import {
    OrganizationModuleType,
    canEmployeeReadOrganizationModule,
    isOrganizationModuleEnabled,
    isOrganizationModuleMember,
} from 'src/domain/organization';
import {
    UserRole,
    getPrimaryRole,
    isOrganizationAdministrator,
} from 'src/domain/user';
import { useCurrentOrganization } from 'src/view/hooks/useCurrentOrganization';
import { useSignedInUser } from 'src/view/hooks/useSignedInUser';

type ModuleStatus = 'notEnabled';

const allowedRoles = new Set([
    UserRole.creator,
    UserRole.organization,
    UserRole.organizationB2C,
]);

export const ModuleNavigationSection = () => {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const user = useSignedInUser();
    const role = getPrimaryRole(user);
    const { organization, isLoading } = useCurrentOrganization();

    const isOnModulePage =
        pathname.startsWith('/app/calendar') ||
        pathname.startsWith('/app/info-screen') ||
        pathname.startsWith('/app/analytics') ||
        pathname.startsWith('/app/documents');
    const [expanded, setExpanded] = useState(true);

    useEffect(() => {
        if (isOnModulePage) {
            setExpanded(true);
        }
    }, [isOnModulePage]);

    if (!role || !allowedRoles.has(role)) {
        return null;
    }

    const isOrganizationAdmin = isOrganizationAdministrator(user);
    const calendarEnabled = isOrganizationModuleEnabled(
        organization,
        OrganizationModuleType.calendar,
    );
    const infoScreenEnabled = isOrganizationModuleEnabled(
        organization,
        OrganizationModuleType.infoScreen,
    );
    const calendarAccess =
        isOrganizationAdmin ||
        role !== UserRole.creator ||
        canEmployeeReadOrganizationModule(
            organization,
            OrganizationModuleType.calendar,
            user?.id,
        );
    const infoScreenAccess =
        isOrganizationAdmin ||
        role !== UserRole.creator ||
        canEmployeeReadOrganizationModule(
            organization,
            OrganizationModuleType.infoScreen,
            user?.id,
        );

    const appContentEnabled = isOrganizationModuleEnabled(
        organization,
        OrganizationModuleType.appContent,
    );
    const appContentAccess =
        isOrganizationAdmin ||
        role !== UserRole.creator ||
        canEmployeeReadOrganizationModule(
            organization,
            OrganizationModuleType.appContent,
            user?.id,
        );

    const analyticsEnabled = isOrganizationModuleEnabled(
        organization,
        OrganizationModuleType.analytics,
    );
    // Members only: Analytics has no "all employees can read" option.
    const analyticsAccess = isOrganizationModuleMember(
        organization,
        OrganizationModuleType.analytics,
        user?.id,
    );
    const getAdminStatus = (enabled: boolean): ModuleStatus | undefined => {
        if (isLoading) return undefined;
        return enabled ? undefined : 'notEnabled';
    };

    const modules: {
        screen: string;
        label: string;
        icon: LucideIcon;
        status?: ModuleStatus;
    }[] = [
        ...(isOrganizationAdmin || (appContentEnabled && appContentAccess)
            ? [
                  {
                      screen: '/app/documents',
                      label: t('modules.appContent.navLabel'),
                      icon: FileText,
                      status: isOrganizationAdmin
                          ? getAdminStatus(appContentEnabled)
                          : undefined,
                  },
              ]
            : []),
        ...(isOrganizationAdmin || (infoScreenEnabled && infoScreenAccess)
            ? [
                  {
                      screen: '/app/info-screen',
                      label: t('modules.infoScreen.navLabel'),
                      icon: Monitor,
                      status: isOrganizationAdmin
                          ? getAdminStatus(infoScreenEnabled)
                          : undefined,
                  },
              ]
            : []),
        ...(isOrganizationAdmin || (calendarEnabled && calendarAccess)
            ? [
                  {
                      screen: '/app/calendar',
                      label: t('modules.calendar.navLabel'),
                      icon: Calendar,
                      status: isOrganizationAdmin
                          ? getAdminStatus(calendarEnabled)
                          : undefined,
                  },
              ]
            : []),
        ...(isOrganizationAdmin || (analyticsEnabled && analyticsAccess)
            ? [
                  {
                      screen: '/app/analytics',
                      label: t('modules.analytics.navLabel'),
                      icon: ChartColumnBig,
                      status: isOrganizationAdmin
                          ? getAdminStatus(analyticsEnabled)
                          : undefined,
                  },
              ]
            : []),
    ];

    if (!modules.length) {
        return null;
    }

    if (!isOrganizationAdmin && modules.length === 1) {
        const [{ screen, label, icon }] = modules;
        const isActive =
            pathname === screen || pathname.startsWith(`${screen}/`);

        return (
            <Link
                to={screen}
                aria-current={isActive ? 'page' : undefined}
                className={sidebarItemClassName(isActive)}
            >
                <SidebarIcon icon={icon} />
                <p className="tracking-tight font-body align-middle text-left flex-1 whitespace-nowrap">
                    {label}
                </p>
            </Link>
        );
    }

    return (
        <>
            <SidebarDivider />
            <button
                type="button"
                aria-expanded={expanded}
                aria-controls="module-navigation-items"
                onClick={() => setExpanded(current => !current)}
                className={sidebarItemClassName(false)}
            >
                <SidebarIcon icon={LayoutGrid} />
                <p className="tracking-tight font-body align-middle text-left flex-1 whitespace-nowrap">
                    {t('modules.moduleNavigation.title')}
                </p>
                <SidebarIcon
                    icon={ChevronDown}
                    className={cx(
                        'transition-transform duration-200',
                        expanded ? 'rotate-180' : 'rotate-0',
                    )}
                />
            </button>

            <ul
                id="module-navigation-items"
                className={cx(
                    'flex flex-col gap-2 w-full transition-all duration-300',
                    expanded
                        ? 'max-h-96 opacity-100'
                        : 'max-h-0 opacity-0 overflow-hidden',
                )}
            >
                {modules.map(({ screen, label, icon, status }) => {
                    const isActive =
                        pathname === screen ||
                        pathname.startsWith(`${screen}/`);
                    const statusLabel = status
                        ? t(`modules.moduleNavigation.${status}`)
                        : undefined;

                    return (
                        <li key={screen}>
                            <Link
                                to={screen}
                                aria-current={isActive ? 'page' : undefined}
                                aria-label={
                                    statusLabel
                                        ? `${label}, ${statusLabel}`
                                        : label
                                }
                                className={sidebarItemClassName(isActive)}
                            >
                                <SidebarIcon icon={icon} />
                                <span className="flex-1 truncate tracking-tight">
                                    {label}
                                </span>
                                {statusLabel && (
                                    <span className="shrink-0 rounded-full border border-current px-1.5 text-[10px] leading-4 whitespace-nowrap">
                                        {statusLabel}
                                    </span>
                                )}
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </>
    );
};
